// Ecosystem Edge §09 — Sika Verified Return Certificate share hook.
//
// Auth-only. On demand (NOT on mount — minting is an explicit user
// action) calls get_or_create_sika_certificate_share(p_goal_id),
// which is SECURITY DEFINER and refuses any goal that is not
// caller-owned AND status='completed' (raises — no fabricated
// certificate). Idempotent: repeated calls return the SAME stable
// token. Returns the opaque share_token; the public card URL is
// derived from it via shareCertificateEndpoint.

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface UseSikaCertificateShareState {
  token: string | null;
  loading: boolean;
  error: string | null;
  /** Mint (or fetch the existing) share token for a completed goal. */
  mint: (goalId: string) => Promise<string | null>;
}

export function useSikaCertificateShare(): UseSikaCertificateShareState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const mint = useCallback(
    async (goalId: string): Promise<string | null> => {
      if (!user || !goalId) {
        setError("Sign in to share this certificate.");
        return null;
      }
      setLoading(true);
      setError(null);
      const { data, error: rpcErr } = await (supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>)(
        "get_or_create_sika_certificate_share",
        { p_goal_id: goalId },
      );
      if (rpcErr) {
        // Honest surface: the RPC raises for non-completed/unowned
        // goals — no fabricated certificate is ever produced.
        setError(rpcErr.message);
        setLoading(false);
        return null;
      }
      const tok = typeof data === "string" ? data : null;
      setToken(tok);
      setLoading(false);
      return tok;
    },
    [supabase, user],
  );

  return { token, loading, error, mint };
}
